/**
 * iter-338i+: ProfitabilityTrendCard — project-dashboard line chart showing
 * Estimated vs Actual margin % across every BOQ in the project. Owner / Admin /
 * Staff only. Falls back to a friendly empty state when the project has no
 * BOQs or none of them have items yet.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceLine,
} from 'recharts';
import { TrendingUp, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtCompact = (n) => {
  const v = Math.abs(+n || 0);
  if (v >= 1e7) return `${((+n) / 1e7).toFixed(1)}Cr`;
  if (v >= 1e5) return `${((+n) / 1e5).toFixed(1)}L`;
  if (v >= 1e3) return `${((+n) / 1e3).toFixed(1)}k`;
  return `${Math.round(+n || 0)}`;
};

export default function ProfitabilityTrendCard({ projectId }) {
  const navigate = useNavigate();
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/projects/${projectId}/profitability-trend`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!cancelled) setTrend(r.data);
      } catch {
        if (!cancelled) setTrend({ series: [], rollup: {}, worksheet_count: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  if (loading || !trend) {
    return (
      <Card data-testid="profitability-trend-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp size={18} className="text-emerald-700" />
            Profitability Pulse
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-stone-500 py-6">Loading trend…</CardContent>
      </Card>
    );
  }

  const series = trend.series || [];
  const rollup = trend.rollup || {};
  const hasData = series.length > 0;
  const chartData = series.map((s, i) => ({
    label: s.display_name || s.worksheet_number || `BOQ ${i + 1}`,
    est_margin: s.est_margin_pct,
    act_margin: s.act_margin_pct,
    est_profit: s.est_profit,
    act_profit: s.act_profit,
    uncategorized: s.uncategorized_count,
    worksheet_id: s.worksheet_id,
  }));

  // iter-338i++: click-through — deep-link to a BOQ's Profitability tab
  const goToBoqProfitability = (worksheetId) => {
    if (!worksheetId) return;
    navigate(`/boq-worksheets/${worksheetId}/edit?openProfitability=1`);
  };
  const handleChartClick = (e) => {
    // recharts passes activePayload with the hovered point
    const wid = e?.activePayload?.[0]?.payload?.worksheet_id;
    if (wid) goToBoqProfitability(wid);
  };
  const handleDotClick = (data) => {
    if (data?.payload?.worksheet_id) goToBoqProfitability(data.payload.worksheet_id);
  };

  const drift = (rollup.act_margin_pct || 0) - (rollup.est_margin_pct || 0);
  const driftPositive = drift >= 0;

  return (
    <Card
      className={`border-2 ${hasData ? (driftPositive ? 'border-emerald-300 bg-emerald-50/40' : 'border-amber-300 bg-amber-50/40') : 'border-stone-200'}`}
      data-testid="profitability-trend-card"
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp size={18} className={driftPositive ? 'text-emerald-700' : 'text-amber-700'} />
            Profitability Pulse
            <span className="text-xs font-normal text-stone-500">· {trend.worksheet_count || 0} BOQ(s)</span>
          </CardTitle>
          {hasData && (
            <div className="flex items-center gap-3 text-xs">
              <div data-testid="trend-rollup-est">
                <span className="text-stone-500">Est. Margin: </span>
                <strong className="text-blue-700">{rollup.est_margin_pct}%</strong>
              </div>
              <div data-testid="trend-rollup-act">
                <span className="text-stone-500">Actual Margin: </span>
                <strong className={driftPositive ? 'text-emerald-700' : 'text-red-700'}>{rollup.act_margin_pct}%</strong>
              </div>
              <div
                className={`px-2 py-0.5 rounded font-semibold ${driftPositive ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}
                data-testid="trend-rollup-drift"
              >
                Drift {driftPositive ? '+' : ''}{drift.toFixed(2)} pp
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!hasData && (
          <p className="text-sm text-stone-500 py-6 text-center">
            No BOQs with line items yet — once items + vendor payments are recorded, the margin trend will appear here.
          </p>
        )}
        {hasData && (
          <>
            <div className="h-56 -mx-2 cursor-pointer" data-testid="trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: -10, bottom: 4 }} onClick={handleChartClick}>
                  <CartesianGrid stroke="#e7e5e4" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10, fill: '#57534e' }}
                    interval={0}
                    angle={chartData.length > 4 ? -25 : 0}
                    textAnchor={chartData.length > 4 ? 'end' : 'middle'}
                    height={chartData.length > 4 ? 60 : 30}
                  />
                  <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: '#57534e' }} />
                  <Tooltip
                    formatter={(v, key) => [`${(+v || 0).toFixed(2)}%`, key === 'est_margin' ? 'Estimated' : 'Actual']}
                    labelFormatter={(lbl) => `${lbl}  (click to open)`}
                    contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: '#d6d3d1' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <ReferenceLine y={0} stroke="#a8a29e" strokeDasharray="4 4" />
                  <Line
                    type="monotone" dataKey="est_margin" name="Estimated Margin %"
                    stroke="var(--brand-button, #1e40af)" strokeWidth={2}
                    dot={{ r: 3, cursor: 'pointer' }}
                    activeDot={{ r: 6, cursor: 'pointer', onClick: (_, p) => handleDotClick(p) }}
                  />
                  <Line
                    type="monotone" dataKey="act_margin" name="Actual Margin %"
                    stroke="var(--brand-button, #047857)" strokeWidth={2.5}
                    dot={{ r: 4, cursor: 'pointer' }}
                    activeDot={{ r: 7, cursor: 'pointer', onClick: (_, p) => handleDotClick(p) }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[11px] text-stone-500 italic mt-1 mb-2" data-testid="trend-clickhint">
              Tip: click any BOQ chip below (or a chart point) to jump straight to that BOQ&apos;s Profitability tab.
            </p>
            {/* iter-338i++: clickable BOQ chips — robust deep-link surface in case the
               recharts onClick is preempted by the tooltip overlay. */}
            <div className="flex flex-wrap gap-1.5 mb-3" data-testid="trend-chip-list">
              {series.map((s) => {
                const positive = (s.act_margin_pct || 0) >= 0;
                return (
                  <button
                    key={s.worksheet_id}
                    type="button"
                    onClick={() => goToBoqProfitability(s.worksheet_id)}
                    className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${positive ? 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-900' : 'border-red-200 bg-red-50 hover:bg-red-100 text-red-900'}`}
                    title={`Estimated ${s.est_margin_pct}% · Actual ${s.act_margin_pct}%`}
                    data-testid={`trend-chip-${s.worksheet_id}`}
                  >
                    <span className="font-medium">{s.display_name || s.worksheet_number}</span>
                    <span className="mx-1 text-stone-400">·</span>
                    <span className={positive ? 'text-emerald-700' : 'text-red-700'}>{s.act_margin_pct}%</span>
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">
              <div className="rounded border border-stone-200 bg-white px-3 py-2">
                <div className="text-stone-500">Est. Profit</div>
                <div className="font-semibold text-blue-700">Rs {fmtCompact(rollup.est_profit)}</div>
              </div>
              <div className="rounded border border-stone-200 bg-white px-3 py-2">
                <div className="text-stone-500">Actual Profit</div>
                <div className={`font-semibold ${rollup.act_profit < 0 ? 'text-red-700' : 'text-emerald-700'}`}>Rs {fmtCompact(rollup.act_profit)}</div>
              </div>
              <div className="rounded border border-stone-200 bg-white px-3 py-2">
                <div className="text-stone-500">Vendor Paid</div>
                <div className="font-semibold text-stone-800">Rs {fmtCompact(rollup.act_vendor_paid)}</div>
              </div>
              <div className="rounded border border-stone-200 bg-white px-3 py-2">
                <div className="text-stone-500">Client Revenue (Act.)</div>
                <div className="font-semibold text-stone-800">Rs {fmtCompact(rollup.act_client_revenue)}</div>
              </div>
            </div>
            {(() => {
              const totalUncat = series.reduce((s, x) => s + (x.uncategorized || 0), 0);
              if (totalUncat === 0) return null;
              return (
                <div className="mt-3 flex items-start gap-2 text-xs rounded border border-amber-300 bg-amber-50 px-3 py-2" data-testid="trend-uncat-warning">
                  <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" />
                  <span className="text-amber-900">
                    <strong>{totalUncat}</strong> vendor payment(s) across the project still need a Legend Category assignment.
                    Open the relevant BOQ&apos;s <em>Profitability</em> tab and assign them for accurate Actual Margins.
                  </span>
                </div>
              );
            })()}
          </>
        )}
      </CardContent>
    </Card>
  );
}
